import {
    applyConditionalFormatting,
    buildDataTableObject,
    Cell,
    TableData,
} from 'utils/data'
import { getGreenfieldLocationFilter } from 'utils/greenfield'
import { isCompany } from 'utils/location'
import { adaptStatusColorToIcon } from 'utils/style-adapters'
import Icon, { CheckmarkIcon, CloseIcon } from '@design-systems/react-icons'
import {
    GreenfieldCardCell,
    GreenfieldCardTable,
    ShipFromStoreData,
} from 'types/store-fulfillment'

export const buildTablesForToday = (
    data: ShipFromStoreData | null | undefined,
    t: (key: string) => string,
    headings: Record<string, string>,
) => {
    const carrierHeadings = [
        t(headings.shipByTime),
        t(headings.pick),
        t(headings.pack),
        t(headings.total),
    ].map((heading) => ({ value: heading }))

    const teamMemberProductivityHeadings = [
        t(headings.teamMember),
        t(headings.attemptedUnits),
        t(headings.picksPerHour),
        t(headings.infPercent),
        t(headings.unitsPacked),
        t(headings.packsPerHour),
    ].map((heading) => ({ value: heading }))

    const shipByTimeTable: TableData = {
        headings: carrierHeadings,
        rows:
            data?.ship_from_store_ship_by?.map((item) => [
                { value: String(item.timestamp ?? '') },
                {
                    value: String(
                        item.due_details?.to_be_picked?.open_units ?? 0,
                    ),
                },
                {
                    value: String(
                        item.due_details?.to_be_packed?.open_units ?? 0,
                    ),
                },
                { value: String(item.due_details?.total?.open_units ?? 0) },
            ]) ?? [],
    }
    const totalHeadings = [t(headings.total)].map((heading) => ({
        value: heading,
    }))
    const secondTable: TableData = {
        headings: totalHeadings,
        rows:
            data?.sort_by_store_close?.[0]?.details?.total?.open_units != null
                ? [
                      [
                          {
                              value: String(
                                  data.sort_by_store_close[0].details.total
                                      .open_units,
                              ),
                          },
                      ],
                  ]
                : [],
    }

    const thirdTable: TableData = {
        headings: totalHeadings,
        rows:
            data?.sort_by_store_close?.[0] &&
            data.open_units?.open_units_details
                ? [
                      [
                          {
                              value: Math.max(
                                  0,
                                  (data.open_units.open_units_details.find(
                                      (detail) =>
                                          detail.open_unit_header === 'total',
                                  )?.total ?? 0) -
                                      (data.sort_by_store_close[0].details
                                          ?.total?.open_units != null
                                          ? Number(
                                                data.sort_by_store_close[0]
                                                    .details?.total?.open_units,
                                            )
                                          : 0),
                              ).toString(),
                          },
                      ],
                  ]
                : [],
    }
    // Fourth table: Team member productivity
    const fourthTable: TableData = {
        headings: teamMemberProductivityHeadings,
        rows:
            data?.team_member_productivity?.team_member_productivity_details?.map(
                (member) => [
                    { value: String(member.tm_name ?? '') },
                    { value: String(member.units_attempted ?? 0) },
                    { value: String(member.sfs_pick_productivity ?? 0) },
                    {
                        ...applyConditionalFormatting(
                            String(member.inf_percentage ?? '0'),
                            member.inf_percentage_color ?? undefined,
                        ),
                    },
                    { value: String(member.units_packed ?? 0) },
                    { value: String(member.sfs_pack_productivity ?? 0) },
                ],
            ) ?? [],
    }

    return [shipByTimeTable, secondTable, thirdTable, fourthTable]
}

export const buildHistoricalTable = (
    storesHistoricalData: GreenfieldCardTable | null | undefined,
    historicalTableTitles: string[],
) => {
    const getValueByIndex = (
        data: GreenfieldCardTable | null | undefined,
        index: number,
    ) => {
        return data?.rows?.[0]?.[index]?.value ?? '0'
    }
    const tableData: TableData = {
        headings: historicalTableTitles.map((title) => ({ value: title })),
        rows: [
            [
                { value: String(getValueByIndex(storesHistoricalData, 5)) },
                { value: String(getValueByIndex(storesHistoricalData, 4)) },
                { value: String(getValueByIndex(storesHistoricalData, 9)) },
            ],
        ],
    }

    return buildDataTableObject({
        currentData: tableData,
        widths: [100, 100, 100],
    })
}
export const buildHistoricalProductivity = (
    storesHistoricalData: GreenfieldCardTable | null | undefined,
    historicalLabels: string[],
) => {
    const getValueByIndex = (
        data: GreenfieldCardTable | null | undefined,
        index: number | undefined,
    ) => {
        if (index === undefined || index < 0) return '0'
        return data?.rows?.[0]?.[index]?.value ?? '0'
    }

    const getColorByIndex = (
        data: GreenfieldCardTable | null | undefined,
        index: number | undefined,
    ) => {
        if (index === undefined || index < 0) return null
        return data?.rows?.[0]?.[index]?.color ?? null
    }

    return historicalLabels.map((label, index) => {
        const dataIndex = [8, 12, 6, 7][index]
        const value = getValueByIndex(storesHistoricalData, dataIndex)
        const color = getColorByIndex(storesHistoricalData, dataIndex)
        const formattedCell = applyConditionalFormatting(
            value,
            color ?? undefined,
        )
        return {
            label,
            ...formattedCell,
        }
    })
}

export const buildShipOnTimeTable = (
    data: GreenfieldCardTable | null | undefined,
    headingTranslations: Record<string, string> = {},
) => {
    if (
        !data?.headings?.length ||
        (!(data.rows?.length ?? 0) && !(data.footer?.length ?? 0))
    ) {
        return null
    }
    const hasLocationColumn =
        String(data.headings[0]?.value ?? '')
            .toLowerCase()
            .trim() === 'location'
    const firstColumnToKeep = hasLocationColumn ? 1 : 0
    const applyStatus = (greenfieldCell: GreenfieldCardCell | null): Cell => {
        const cell: Cell = {
            ...(greenfieldCell
                ? {
                      raw: greenfieldCell.raw ?? undefined,
                      childOf: greenfieldCell.childOf ?? undefined,
                      locationId: greenfieldCell.locationId ?? undefined,
                      className: greenfieldCell.className ?? undefined,
                      id: greenfieldCell.id ?? undefined,
                      icon: greenfieldCell.icon,
                      color: greenfieldCell.color ?? undefined,
                  }
                : {}),
            value: String(greenfieldCell?.value ?? ''),
        }
        const statusIcon = cell.color
            ? adaptStatusColorToIcon(cell.color)
            : undefined
        if (!statusIcon) return cell

        const statusClass =
            statusIcon === CheckmarkIcon
                ? 'hc8-clr-success'
                : statusIcon === CloseIcon
                  ? 'hc8-clr-error'
                  : 'hc8-clr-attention'

        return {
            ...cell,
            className: [cell.className, statusClass].filter(Boolean).join(' '),
            icon: (
                <Icon
                    svg={statusIcon}
                    className={statusClass}
                    size="sm"
                    aria-hidden
                />
            ),
        }
    }

    const translatedData: TableData = {
        ...data,
        headings: data.headings
            .slice(firstColumnToKeep)
            .map((greenfieldHeading) => {
                const headingValue = String(greenfieldHeading?.value ?? '')
                return {
                    ...(greenfieldHeading
                        ? {
                              raw: greenfieldHeading.raw ?? undefined,
                              childOf: greenfieldHeading.childOf ?? undefined,
                              locationId:
                                  greenfieldHeading.locationId ?? undefined,
                              color: greenfieldHeading.color ?? undefined,
                              className:
                                  greenfieldHeading.className ?? undefined,
                              id: greenfieldHeading.id ?? undefined,
                              icon: greenfieldHeading.icon,
                          }
                        : {}),
                    value:
                        headingTranslations[
                            headingValue.toLowerCase().trim()
                        ] ?? headingValue,
                }
            }),
        rows: (data.rows ?? []).map((row) =>
            (row ?? []).slice(firstColumnToKeep).map(applyStatus),
        ),
        footer: undefined,
    }

    return buildDataTableObject({
        currentData: translatedData,
        widths: translatedData.headings?.map(() => 140) ?? [],
    })
}

export const getShipOnTimeInterval = (timePeriod: string): string => {
    const intervals: Record<string, string> = {
        prior_day: 'Prior Day',
        week_to_day: 'Week to Day',
        prior_week: 'Prior Week',
        month_to_day: 'Month to Day',
        year_to_day: 'Year to Day',
    }

    return intervals[timePeriod] ?? 'Prior Week'
}

export const getShipOnTimeFilters = (
    location: string,
    timePeriod: string,
): Record<string, string> => ({
    ...(!isCompany(location) && getGreenfieldLocationFilter(location)),
    $timePeriod$calendar_type: 'Fiscal',
    type: 'relative',
    interval: getShipOnTimeInterval(timePeriod),
    time_dimension: timePeriod,
})

export const clamp = (n: number, min = 0, max = 100) =>
    Math.max(min, Math.min(max, n))
export const pct = (num: number, denom: number) =>
    denom > 0 ? (num / denom) * 100 : 0
export const toPctStr = (n: number) => `${clamp(n, 0, 100).toFixed(2)}%`
export const fmtInt = (n: number) => new Intl.NumberFormat('en-US').format(n)
